import { Button } from "@components/button";
import { withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext from "@odata/BindingContext";
import {
    IAddressEntity,
    IPrCaregivingEventEntity,
    IPrIllnessLeaveEventEntity,
    PrIllnessLeaveEventEntity
} from "@odata/GeneratedEntityTypes";
import { CountryCode, PrSocialEventTypeCode } from "@odata/GeneratedEnums";
import { getCurrentRangeIndex, TTemporal, updateTemporalRangesAfterCurrentTempPropChange } from "@odata/TemporalUtils";
import {
    CurrentCityLocalPath,
    CurrentCountryLocalPath,
    CurrentLocalAddressFields,
    CurrentPostalCodeLocalPath,
    CurrentStreetLocalPath,
    hasAdditionalFlagsLocalPath,
    hasDoctorLocalPath,
    hasResidenceAddressLocalPath,
    isDoctorAddressFilled
} from "@pages/payroll/socialEvents/eventTypes/IllnessLeave.utils";
import {
    handleDurationIntervalLineItemsAction,
    handleDurationIntervalToggle,
    initMonthDetailsAfterLoad
} from "@pages/payroll/socialEvents/SocialEvents.utils";
import { SocialEventsFormViewForExtend } from "@pages/payroll/socialEvents/SocialEventsFormView";
import React from "react";

import { withPermissionContext } from "../../../../contexts/permissionContext/withPermissionContext";
import { FormStorage } from "../../../../views/formView/FormStorage";

class IllnessLeaveFormView extends SocialEventsFormViewForExtend<IPrIllnessLeaveEventEntity> {
    typeCode = PrSocialEventTypeCode.IllnessLeave;

    async onAfterLoad(): Promise<void> {
        this.initLocalPaths();
        this.setCurrentResidenceAddressFieldValues();

        initMonthDetailsAfterLoad(this.props.storage);
        return super.onAfterLoad();
    }

    initLocalPaths() {
        const { storage } = this.props;
        const { entity } = storage.data;
        if (entity.DoctorName || entity.HealthcareProviderName || isDoctorAddressFilled({ storage })) {
            storage.setValueByPath(hasDoctorLocalPath, true);
        }
        if (entity.IsWorkplaceInjury || entity.IsCausedByAlcoholOrDrugs || entity.IsCausedByAnotherPerson) {
            storage.setValueByPath(hasAdditionalFlagsLocalPath, true);
        }
        if (entity.ResidenceAddresses?.length) {
            storage.setValueByPath(hasResidenceAddressLocalPath, true);
        }
    }

    setCurrentResidenceAddressFieldValues(current?: IAddressEntity): void {
        if (!current && this.entity.ResidenceAddresses?.length) {
            current = this.entity.ResidenceAddresses[getCurrentRangeIndex(this.entity.ResidenceAddresses as TTemporal[])];
        }
        this.props.storage.setValueByPath(CurrentStreetLocalPath, current?.Street);
        this.props.storage.setValueByPath(CurrentCityLocalPath, current?.City);
        this.props.storage.setValueByPath(CurrentPostalCodeLocalPath, current?.PostalCode);
        this.props.storage.setValueByPath(CurrentCountryLocalPath, current?.Country?.Code ?? CountryCode.CzechRepublic);
    }

    handleCurrentResidenceAddressFieldChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();

        if (!CurrentLocalAddressFields.includes(path)) {
            return;
        }

        const parentBc = e.bindingContext.getParent().navigate(PrIllnessLeaveEventEntity.ResidenceAddresses);
        const fieldPath = BindingContext.cleanLocalContext(path);
        const fieldBc = parentBc.navigate(fieldPath);

        updateTemporalRangesAfterCurrentTempPropChange({
            value: e.value,
            bindingContext: fieldBc,
            additionalData: e.additionalData
        }, storage, PrIllnessLeaveEventEntity.ResidenceAddresses);

        storage.refresh();
    };

    handleChange = (e: ISmartFieldChange): void => {
        this.handleCurrentResidenceAddressFieldChange(e);
        handleDurationIntervalToggle(this.props.storage, e);

        super.handleChange(e);
        // keep after super, so change is applied to storage before handling the dependencies
    };

    handleLineItemsAction(args: ISmartFastEntriesActionEvent): void {
        handleDurationIntervalLineItemsAction(this.props.storage, args);
        super.handleLineItemsAction(args);
    }

    onBeforeSave(): IPrCaregivingEventEntity {
        const { storage } = this.props;
        const entity = super.onBeforeSave();
        if (!isDoctorAddressFilled({ storage })) {
            entity.DoctorAddress = null;
        }
        if (!storage.getValueByPath(hasResidenceAddressLocalPath)) {
            entity.ResidenceAddresses = [];
        } else {
            entity.ResidenceAddresses?.forEach(address => {
                if (!address.Country?.Code && !address.CountryCode) {
                    // default value of a newly created range if not filled by the user
                    address.CountryCode = CountryCode.CzechRepublic;
                }
            });
            storage.setValueByPath(PrIllnessLeaveEventEntity.ResidenceAddresses, entity.ResidenceAddresses);
        }
        return entity;
    }

    handleSmartTemporalPropertyDialogClose = (_: BindingContext, current?: TTemporal<IAddressEntity>): void => {
        this.setCurrentResidenceAddressFieldValues(current);
    };

    handleEventCancelToggle = () => {
        const storage = this.props.storage as FormStorage<IPrIllnessLeaveEventEntity>;
        const { IsCancelled } = storage.data.entity;

        storage.data.entity.IsCancelled = !IsCancelled;
        this.save();
    }

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const { DateEventEnd, IsCancelled } = storage.data.origEntity ?? {};

        const showCancelButton = false; // todo: when ready... !isNewEntity({ storage }) && !DateEventEnd;
        const cancelButton: React.ReactElement = showCancelButton ? (
            <Button hotspotId={"cancelEvent"}
                    onClick={this.handleEventCancelToggle}
                    isTransparent>
                {storage.t(IsCancelled ? "SocialEvents:Form.ReopenEvent" : "SocialEvents:Form.CancelEvent")}
            </Button>
        ) : null;

        return this.renderSocialEventButtons(cancelButton, !IsCancelled);
    }
}

export default withPermissionContext(withConfirmationDialog(IllnessLeaveFormView));